import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { format } from 'date-fns/format';
import can from 'ember-can/helpers/can';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import EmberPopover from 'ember-tooltips/components/ember-popover';
import { and, or } from 'ember-truth-helpers';
import MonogramOrPhoto from 'garaje/components/monogram-or-photo';
import type VisitorProfileModel from 'garaje/models/visitor-profile';
import type VisitStatsService from 'garaje/services/visit-stats';
import type { VisitorProfileStats } from 'garaje/types/visitor-profile-stats';

import ViewProfileLink from './view-profile-link';

const HIGHLIGHT_DATE_FORMAT = 'MMM d, yyyy'; // e.g. "Apr 18, 2026"
const HIGHLIGHT_MONTH_FORMAT = 'MMM yyyy'; // e.g. "Aug 2026"

interface HoverCardSignature {
  Args: {
    /**
     * The visitor profile to display in the card.
     * Must be pre-loaded (sync) by the parent — either via sideload or inline.
     */
    visitorProfile: VisitorProfileModel | null;
    /**
     * `from` query param forwarded to ViewProfileLink (e.g. 'invite', 'entry').
     */
    from: string;
    /**
     * The source record id forwarded to ViewProfileLink for back navigation,
     * and used as the model for the "View visit" LinkTo.
     */
    id: string;
    /**
     * Optional fallback name for the header when the sideloaded
     * `visitorProfile.displayName` is empty. The backend's `display_name`
     * column has no NOT NULL constraint and can be blank for walk-up
     * entries / CSV imports / profile-merge edge cases — the parent
     * row's invite/entry `fullName` is always populated, so passing it
     * here keeps the header from rendering an empty span.
     */
    fallbackName?: string;
    /**
     * Route name for the "View visit" button (e.g. 'visitors.invites.show').
     * When omitted the "View visit" button is not rendered.
     */
    visitRoute?: string;
    /**
     * Override the popper container selector. Defaults to '.ui__page'.
     */
    popperContainer?: string;
  };
  Blocks: {
    /**
     * Block content rendered as the popover trigger (wrapped in a div).
     */
    default: [];
  };
}

/**
 * Hover card for a visitor profile shown over a visitor's name in the invites
 * and visitor log tables. Stats and the optional internal note are fetched
 * lazily on first mouseenter — listing N rows doesn't dispatch N requests.
 *
 * Parent is responsible for only mounting this component when the user has
 * `read visitor-profile`; the popover surfaces visitor-profile-scoped data
 * (email, phone, stats, notes) and other entry points gate on the same
 * ability.
 */
export default class VisitorProfileHoverCard extends Component<HoverCardSignature> {
  @service('visit-stats') declare visitStatsService: VisitStatsService;
  @service declare store: StoreService;

  @tracked stats: VisitorProfileStats | null = null;
  @tracked statsLoading = false;

  @tracked note: string | null = null;
  @tracked noteLoading = false;
  // `note === null` is both the initial state AND the "no note exists" terminal
  // state, so we need a separate boolean to distinguish "haven't fetched yet"
  // from "fetched and got null". Without this, every mouseenter on a profile
  // without a note re-runs the promise chain (network is cached but the
  // callbacks re-fire and `noteLoading` flickers).
  @tracked noteFetched = false;
  // Enrichment guard: invite/entry payloads sideload `visitor-profile` with a
  // sparse fieldset (no displayName / photoUrl). On first hover we reload the
  // record so the in-store model gets full data — Glimmer re-renders the
  // header automatically because the model identity is stable.
  @tracked profileEnriched = false;

  @action
  loadHoverData(): void {
    const profileId = this.args.visitorProfile?.id;
    if (!profileId) return;

    if (!this.stats && !this.statsLoading) {
      this.statsLoading = true;
      this.visitStatsService
        .fetch(profileId)
        .then((stats) => {
          if (this.isDestroyed || this.isDestroying) return;
          this.stats = stats;
        })
        .catch(() => {
          // hasAnyHighlight returns false while stats is null, so the
          // Highlights section stays hidden. No tracked write needed.
        })
        .finally(() => {
          if (this.isDestroyed || this.isDestroying) return;
          this.statsLoading = false;
        });
    }

    if (!this.profileEnriched) {
      this.profileEnriched = true;
      this.store.findRecord('visitor-profile', profileId, { reload: true }).catch(() => {
        // Silent — header falls back to monogram + fallbackName.
        // Reset so a future hover after backend recovery can retry.
        if (this.isDestroyed || this.isDestroying) return;
        this.profileEnriched = false;
      });
    }

    if (!this.noteFetched && !this.noteLoading) {
      this.noteLoading = true;
      this.visitStatsService
        .fetchNote(profileId)
        .then((note) => {
          if (this.isDestroyed || this.isDestroying) return;
          this.note = note;
          this.noteFetched = true;
        })
        .catch(() => {
          // Swallow: the service maps 404 → resolved null already, so this
          // branch only fires for 5xx / network failure on a row-level
          // endpoint. Logging from a hover handler runs once per hovered row
          // and pollutes the console without giving an actionable signal.
          // The Internal note section stays hidden because `note` remains
          // null — that's the user-visible failure mode.
        })
        .finally(() => {
          if (this.isDestroyed || this.isDestroying) return;
          this.noteLoading = false;
        });
    }
  }

  get totalVisits(): number {
    return this.stats?.previousSignInsCount ?? 0;
  }

  get totalVisitsLabel(): string {
    const n = this.totalVisits;
    return `${n} ${n === 1 ? 'time' : 'times'} on-site`;
  }

  get formattedFirstVisitDate(): string | null {
    return this.#formatDate(this.stats?.firstVisitDate, HIGHLIGHT_DATE_FORMAT);
  }

  get formattedLastVisitDate(): string | null {
    return this.#formatDate(this.stats?.lastVisitDate, HIGHLIGHT_DATE_FORMAT);
  }

  /**
   * Figma: "Aug 2026 • recurring" / "Aug 2026 • non-recurring".
   * Suffix uses the upcoming-visits breakdown — recurring wins ties because the
   * ProfileStats backend treats any recurring visit as the dominant cadence.
   */
  get formattedNextUpcoming(): string | null {
    const dt = this.#formatDate(this.stats?.nextUpcomingVisitDateTime, HIGHLIGHT_MONTH_FORMAT);
    if (!dt) return null;
    const counts = this.stats?.upcomingVisitsCount;
    if (!counts) return dt;
    if (counts.recurringCount > 0) return `${dt} • recurring`;
    if (counts.nonRecurringCount > 0) return `${dt} • non-recurring`;
    return dt;
  }

  get hasAnyHighlight(): boolean {
    if (!this.stats) return false;
    return (
      this.stats.previousSignInsCount > 0 ||
      Boolean(this.stats.firstVisitDate) ||
      Boolean(this.stats.lastVisitDate) ||
      Boolean(this.stats.nextUpcomingVisitDateTime) ||
      Boolean(this.stats.frequentLocationName) ||
      Boolean(this.stats.frequentHostName)
    );
  }

  #formatDate(raw: string | null | undefined, pattern: string): string | null {
    if (!raw) return null;
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return null;
    return format(date, pattern);
  }

  // Safe under the `(and @visitorProfile (can "read visitor-profile"))` template
  // guard above the popover; the empty-string fallback is unreachable at runtime
  // but lets ember-tsc narrow `id: string | undefined` → `string` for the
  // ViewProfileLink @visitorId signature.
  get visitorProfileId(): string {
    return this.args.visitorProfile?.id ?? '';
  }

  /**
   * Display name with fallback to the parent row's `fullName`. Backend
   * `display_name` can legitimately be empty for walk-ups / CSV imports —
   * falling back keeps the header from rendering an empty space next to
   * the avatar. Empty-string `?.trim()` ensures whitespace-only values
   * also fall through to the fallback.
   */
  get displayName(): string {
    const profileName = this.args.visitorProfile?.displayName?.trim();
    if (profileName) return profileName;
    return this.args.fallbackName?.trim() || '';
  }

  <template>
    {{! Wrapper div is required — EmberPopover uses element.parentNode as the anchor.
        inline-block + w-fit forces the wrapper to size to content even when the
        yielded link uses display:block (entries) — without w-fit, a block child
        stretches the inline-block parent to the table cell width, leaving
        ~200px visible whitespace between the visible name and the popover.
        With w-fit both invites (flex child) and entries (block child) get a
        tight wrapper, so the popover anchors next to the visible text. }}
    <div class="inline-block w-fit" {{on "mouseenter" this.loadHoverData}}>
      {{yield}}
      {{! Defense-in-depth: gate the popover on `read visitor-profile` here as
          well as at the parent (shouldShowVisitorProfileHoverCard already
          enforces this). Belt-and-suspenders so a future caller that bypasses
          the parent helper can't accidentally leak email/phone/stats/notes. }}
      {{#if (and @visitorProfile (can "read visitor-profile"))}}
        <EmberPopover
          @popperContainer={{or @popperContainer ".ui__page"}}
          @side="right"
          @spacing={{2}}
          @tooltipClass="z-50 rounded-[7px] shadow-[0_3.646px_7.292px_0_rgba(0,0,0,0.12)] bg-white border border-carbon-10 w-96 p-0 [&_.ember-popover-arrow]:hidden"
          data-test-visitor-profile-hover-card
        >
          <div class="flex items-center gap-2 px-4 pt-[10px] pb-[10px]">
            <MonogramOrPhoto
              @borderBottom={{false}}
              @name={{this.displayName}}
              @passportPhoto={{@visitorProfile.photoUrl}}
              @rounded="full"
            />
            <span class="font-semibold text-base text-carbon-70 truncate" data-test-hover-card-name>
              {{this.displayName}}
            </span>
          </div>

          {{! Uses dedicated hover-card-email/hover-card-phone svg-jar assets
              instead of ui-email/ui-phone — those have a hardcoded
              <clipPath id="clip0_708_…"> which collides when multiple hover
              cards instance the same icon (the second card's clip-path resolves
              to the first card's defs, and after the first card unmounts the
              icon disappears from remaining cards). The hover-card-* variants
              drop the unnecessary clipPath. }}
          {{#if (or @visitorProfile.primaryEmail @visitorProfile.formattedPhoneNumber)}}
            <hr class="border-carbon-10 m-0" />
            <div class="flex flex-col gap-2 px-4 py-5 text-sm text-carbon-40">
              {{#if @visitorProfile.primaryEmail}}
                <div class="flex items-center gap-2 min-w-0" data-test-hover-card-email>
                  {{svgJar "hover-card-email" class="w-4 h-4 shrink-0" aria-hidden="true"}}
                  <span class="truncate">{{@visitorProfile.primaryEmail}}</span>
                </div>
              {{/if}}
              {{#if @visitorProfile.formattedPhoneNumber}}
                <div class="flex items-center gap-2 min-w-0" data-test-hover-card-phone>
                  {{svgJar "hover-card-phone" class="w-4 h-4 shrink-0" aria-hidden="true"}}
                  <span class="truncate">{{@visitorProfile.formattedPhoneNumber}}</span>
                </div>
              {{/if}}
            </div>
          {{/if}}

          {{! Highlights — lazy-loaded; rows individually hidden when their field is null }}
          {{#if this.statsLoading}}
            <hr class="border-carbon-10 m-0" />
            <div
              class="flex items-center justify-center px-4 py-5 text-xs text-carbon-50"
              data-test-hover-card-highlights-loading
            >
              Loading visit stats…
            </div>
          {{else if this.hasAnyHighlight}}
            <hr class="border-carbon-10 m-0" />
            <div class="flex flex-col gap-1 px-4 py-5 text-[12.762px] leading-[150%]" data-test-hover-card-highlights>
              <div class="font-semibold text-sm text-carbon-70 mb-1">Highlights</div>

              {{#if this.totalVisits}}
                <div class="flex justify-between">
                  <span class="text-carbon-60">Total visits</span>
                  <span class="font-semibold text-carbon-80" data-test-hover-card-total-visits>
                    {{this.totalVisitsLabel}}
                  </span>
                </div>
              {{/if}}

              {{#if this.formattedNextUpcoming}}
                <div class="flex justify-between gap-2">
                  <span class="text-carbon-60 shrink-0">Next after today</span>
                  <span class="font-semibold text-carbon-80 text-right" data-test-hover-card-next-after-today>
                    {{this.formattedNextUpcoming}}
                  </span>
                </div>
              {{/if}}

              {{#if this.formattedLastVisitDate}}
                <div class="flex justify-between">
                  <span class="text-carbon-60">Last visited</span>
                  <span class="font-semibold text-carbon-80" data-test-hover-card-last-visited>
                    {{this.formattedLastVisitDate}}
                  </span>
                </div>
              {{/if}}

              {{#if this.formattedFirstVisitDate}}
                <div class="flex justify-between">
                  <span class="text-carbon-60">First visited</span>
                  <span class="font-semibold text-carbon-80" data-test-hover-card-first-visited>
                    {{this.formattedFirstVisitDate}}
                  </span>
                </div>
              {{/if}}

              {{#if this.stats.frequentLocationName}}
                <div class="flex justify-between gap-2">
                  <span class="text-carbon-60 shrink-0">Frequent location</span>
                  <span
                    class="font-semibold text-carbon-80 truncate"
                    data-test-hover-card-frequent-location
                    title={{this.stats.frequentLocationName}}
                  >
                    {{this.stats.frequentLocationName}}
                  </span>
                </div>
              {{/if}}

              {{#if this.stats.frequentHostName}}
                <div class="flex justify-between gap-2">
                  <span class="text-carbon-60 shrink-0">Frequent host</span>
                  <span
                    class="font-semibold text-carbon-80 truncate"
                    data-test-hover-card-frequent-host
                    title={{this.stats.frequentHostName}}
                  >
                    {{this.stats.frequentHostName}}
                  </span>
                </div>
              {{/if}}
            </div>
          {{/if}}

          {{#if this.note}}
            <hr class="border-carbon-10 m-0" />
            <div class="flex flex-col gap-1 px-4 py-5" data-test-hover-card-internal-note>
              <div class="font-semibold text-sm text-carbon-70">Internal note</div>
              <p class="text-[12.762px] leading-[150%] text-carbon-60 line-clamp-2 m-0" title={{this.note}}>
                {{this.note}}
              </p>
            </div>
          {{/if}}

          {{! CTAs — View profile gated to match other visitor-profile entry points (e.g. visitors/entry, visitors/invites/show) }}
          <hr class="border-carbon-10 m-0" />
          <div class="flex items-center justify-end gap-3 px-4 pt-5 pb-[17px]">
            {{#if (can "read visitor-profile")}}
              <ViewProfileLink @visitorId={{this.visitorProfileId}} @from={{@from}} @id={{@id}} />
            {{/if}}
            {{#if @visitRoute}}
              <LinkTo
                @route={{@visitRoute}}
                @model={{@id}}
                class="btn btn--small btn--brand no-underline hover:no-underline"
                data-test-view-visit-link
              >
                View visit
              </LinkTo>
            {{/if}}
          </div>
        </EmberPopover>
      {{/if}}
    </div>
  </template>
}
